// eslint-disable-next-line import/no-named-as-default
import Icon from "@mdi/react";
import { memo, useEffect, useMemo, useRef, useState } from "react";

import { mdiAlertCircleOutline } from "@mdi/js";
import { Input, Popover } from "antd";
import { FIELD_LABEL_GETTERS } from "constants/fieldLabels.constants";
import _debounce from "lodash/debounce";
import { useForecastStore } from "stores/forecast";
import styled from "styled-components/macro";
import { isEmptyValue } from "utils/fp";

import { useUserUnit } from "hooks";
import { useTypeWellUnitConversion } from "hooks/useTypeWellUnitConversion";

import { AdditionalTypeWellData, UserArpsItem, WellData } from "models/UserArpsModel";

import { useSelectedProjectPermissions } from "components/project/projects/hooks";

import { checkAdditionalNormalizationParameters } from "./utils/checkAdditionalNormalizationParameters";
import { AddNormalizationFieldButton } from "./widget/TypeWellEditor/tabs/AddNormalizationFieldButton";
import { TypeWellNormalizationParameter } from "./widget/TypeWellEditor/tabs/TypeWellNormalizationParameter";

export interface ITypeWellData {
  length?: number;
  stage?: number;
  spacing?: number;
  proppant?: number;
  intensity?: number;
}

interface EditTypeWellDataProps {
  selectedTypeWell: UserArpsItem;
  onChange?: (value?: WellData) => void;
}
function EditTypeWellData({ selectedTypeWell = null, onChange }: EditTypeWellDataProps) {
  const projectPermissions = useSelectedProjectPermissions();
  const unitType = useUserUnit();
  const {
    convertLengthToDisplay,
    convertLengthToSource,
    convertProppantIntensityToDisplay,
    convertProppantIntensityToSource
  } = useTypeWellUnitConversion();

  const [lengthError, setLengthError] = useState(null);
  const [spacingError, setSpacingError] = useState(null);
  const [intensityError, setIntensityError] = useState(null);
  const [additionalErrors, setAdditionalErrors] = useState([]);

  const setSelectedTypeWell = useForecastStore((s) => s.setSelectedTypeWell);
  const setHasWellDataError = useForecastStore((s) => s.setHasWellDataError);

  const wellData = selectedTypeWell?.wellData;

  // Display values (converted for UI)
  const displayHzLength = useMemo(
    () => convertLengthToDisplay(wellData?.HzLength),
    [wellData?.HzLength, convertLengthToDisplay]
  );

  const displayStageSpacing = useMemo(
    () => convertLengthToDisplay(wellData?.StageSpacing),
    [wellData?.StageSpacing, convertLengthToDisplay]
  );

  const displayProppantIntensity = useMemo(
    () => convertProppantIntensityToDisplay(wellData?.ProppantIntensity),
    [wellData?.ProppantIntensity, convertProppantIntensityToDisplay]
  );

  const changeDebounceRef = useRef(
    _debounce((fn) => {
      fn && fn();
    }, 600)
  );

  useEffect(() => {
    if (wellData) {
      validateTypeWellData(wellData);
    }
  }, [wellData]);

  const setWellData = (data) => {
    const newWellData = {
      ...wellData,
      ...data
    };

    setSelectedTypeWell({
      ...selectedTypeWell,
      wellData: newWellData
    });
  };

  const validateDefaultTypeWellData = (length, spacing, intensity) => {
    setLengthError(null);
    setSpacingError(null);
    setIntensityError(null);
    let isValid = true;
    if (length && length < 0) {
      setLengthError("Value must be non-negative");
      isValid = false;
    }
    if (intensity && intensity < 0) {
      setIntensityError("Value must be non-negative");
      isValid = false;
    }
    if (spacing && spacing > length && length) {
      setSpacingError("Stage Spacing can't be greater than the Length");
      isValid = false;
    }
    if (spacing && spacing < 0) {
      setSpacingError("Value must be non-negative");
      isValid = false;
    }
    setHasWellDataError(!isValid);
    return isValid;
  };

  const validateAdditionalData = (data: AdditionalTypeWellData[]) => {
    const { errors, isValid } = checkAdditionalNormalizationParameters(data);
    setAdditionalErrors(errors);
    setHasWellDataError(!isValid);
    return isValid;
  };

  const validateTypeWellData = (wellData: WellData) => {
    return (
      validateDefaultTypeWellData(
        wellData.HzLength,
        wellData.StageSpacing,
        wellData.ProppantIntensity
      ) && validateAdditionalData(wellData.AdditionalData)
    );
  };

  const handleChangeLength = (value) => {
    if (isEmptyValue(value)) {
      const updatedWellData: WellData = {
        ...wellData,
        HzLength: undefined,
        Stage: undefined,
        Proppant: undefined
      };
      setWellData(updatedWellData);
      return;
    }

    const displayValue = parseFloat(value);
    if (isNaN(displayValue)) return;

    // Convert from display unit to source unit (meters)
    const newLengthInMeters = convertLengthToSource(displayValue);
    const spacing = wellData?.StageSpacing;
    const intensity = wellData?.ProppantIntensity;

    const updatedWellData: WellData = {
      ...wellData,
      HzLength: newLengthInMeters,
      Stage: spacing ? newLengthInMeters / spacing : wellData?.Stage,
      Proppant: intensity ? intensity * newLengthInMeters : wellData?.Proppant
    };
    setWellData(updatedWellData);

    // Update charts if values are correct.
    if (validateTypeWellData(updatedWellData)) {
      changeDebounceRef.current(() => onChange && onChange(updatedWellData));
    }
  };

  const handleChangeStageSpacing = (value) => {
    if (isEmptyValue(value)) {
      const updatedWellData: WellData = {
        ...wellData,
        Stage: undefined,
        StageSpacing: undefined
      };
      setWellData(updatedWellData);
      return;
    }

    const displayValue = parseFloat(value);
    if (isNaN(displayValue)) return;

    // Convert from display unit to source unit (meters)
    const newSpacingInMeters = convertLengthToSource(displayValue);
    const length = wellData?.HzLength;

    const updatedWellData: WellData = {
      ...wellData,
      Stage: length / newSpacingInMeters,
      StageSpacing: newSpacingInMeters
    };
    setWellData(updatedWellData);

    // Update charts if values are correct.
    if (validateTypeWellData(updatedWellData)) {
      changeDebounceRef.current(() => onChange && onChange(updatedWellData));
    }
  };

  const handleChangeProppantIntensity = (value) => {
    if (isEmptyValue(value)) {
      const updatedWellData: WellData = {
        ...wellData,
        Proppant: undefined,
        ProppantIntensity: undefined
      };
      setWellData(updatedWellData);
      return;
    }

    const displayValue = parseFloat(value);
    if (isNaN(displayValue)) return;

    // Convert from display unit to source unit (t/m)
    const newIntensityInTonnePerMeter = convertProppantIntensityToSource(displayValue);
    const length = wellData?.HzLength;

    const updatedWellData: WellData = {
      ...wellData,
      Proppant: newIntensityInTonnePerMeter * length,
      ProppantIntensity: newIntensityInTonnePerMeter
    };
    setWellData(updatedWellData);

    // Update charts if values are correct.
    if (validateTypeWellData(updatedWellData)) {
      changeDebounceRef.current(() => onChange && onChange(updatedWellData));
    }
  };

  const ErrorPopover = ({ errorMessage }) => {
    return (
      errorMessage && (
        <div style={{ width: "20px" }}>
          <Popover content={<div>{errorMessage}</div>}>
            <ErrorWrapper
              color={errorMessage.toLowerCase().includes("field") ? "orange" : "red"}>
              <Icon path={mdiAlertCircleOutline} />
            </ErrorWrapper>
          </Popover>
        </div>
      )
    );
  };

  return (
    <EditContainer>
      <Wrapper>
        <div>{FIELD_LABEL_GETTERS.HZ_LENGTH(unitType)}</div>
        <InputWrapper
          data-testid="type-well-hz-length-input"
          disabled={!projectPermissions.canEditTypeWells}
          name={FIELD_LABEL_GETTERS.HZ_LENGTH(unitType)}
          value={displayHzLength ?? undefined}
          placeholder={"e.g. 2000"}
          type="number"
          onChange={(e) => handleChangeLength(e.target.value)}
          onDragStart={(e) => e.preventDefault()}
          onDrop={(e) => e.preventDefault()}
        />
        <ErrorPopover errorMessage={lengthError} />
      </Wrapper>
      <Wrapper>
        <div>{FIELD_LABEL_GETTERS.STAGE_SPACING(unitType)}</div>
        <InputWrapper
          disabled={!projectPermissions.canEditTypeWells}
          name={FIELD_LABEL_GETTERS.STAGE_SPACING(unitType)}
          value={displayStageSpacing ?? undefined}
          type="number"
          placeholder={"e.g. 500"}
          onChange={(e) => handleChangeStageSpacing(e.target.value)}
          onDragStart={(e) => e.preventDefault()}
          onDrop={(e) => e.preventDefault()}
        />
        <ErrorPopover errorMessage={spacingError} />
      </Wrapper>
      <Wrapper>
        <div>{FIELD_LABEL_GETTERS.PROPPANT_INTENSITY(unitType)}</div>
        <InputWrapper
          disabled={!projectPermissions.canEditTypeWells}
          name={FIELD_LABEL_GETTERS.PROPPANT_INTENSITY(unitType)}
          value={displayProppantIntensity ?? undefined}
          type="number"
          placeholder={"e.g. 2.5"}
          onChange={(e) => handleChangeProppantIntensity(e.target.value)}
          onDragStart={(e) => e.preventDefault()}
          onDrop={(e) => e.preventDefault()}
        />
      </Wrapper>
      <ErrorPopover errorMessage={intensityError} />

      {wellData?.AdditionalData?.map((_, index) => (
        <Wrapper key={index}>
          <TypeWellNormalizationParameter
            index={index}
            validateTypeWellData={validateTypeWellData}
            onChange={onChange}
          />

          {additionalErrors[index] ? (
            <ErrorPopover errorMessage={additionalErrors[index]} />
          ) : (
            <></>
          )}
        </Wrapper>
      ))}

      <Wrapper>
        <AddNormalizationFieldButton
          validateTypeWellData={validateTypeWellData}
          onChange={onChange}
        />
      </Wrapper>
    </EditContainer>
  );
}

export default memo(EditTypeWellData);

export const EditContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 5px;
  padding: 15px;
`;

const InputWrapper = styled(Input)`
  background-color: #ebf8ef;
  font-size: 1.3rem;
  border-radius: var(--border-radius);
`;

const ErrorWrapper = styled.div`
  display: flex;
  color: ${(props) => props.color};
`;

const Wrapper = styled.div`
  display: grid;
  grid-template-columns: 200px minmax(auto, 500px) 30px 30px;
  position: relative;
  align-items: center;
  gap: 10px;
  width: 100%;

  .error-message {
    padding-left: 10px;
    font-size: 1.2rem;
    margin-top: 5px;
  }

  .ant-input {
    padding-right: 35px !important;
    width: 100%;
  }

  input::-webkit-outer-spin-button,
  input::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }

  /* Firefox */

  input[type="number"] {
    -moz-appearance: textfield;
    appearance: textfield;
  }
`;
